
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { BaseLayout } from '@/components/templates';
import { withUrql } from '@/config/graphql';
import { Container, Heading, Text } from '@chakra-ui/react';
import { TextualHero, TitleSection, TileList, IconName, TileProps, LinkBoxList, LinkBox, TextLink, } from '@meritcircle/ui/components';
import { contentfulClient } from '@cms/client';
import { AcademyPopularGameFieldsFragment, ArticleOverviewPageDocument, ArticleOverviewPageQuery, } from '@cms/graphql/articleOverviewPage.generated';
import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { useRouter } from 'next/router';
import { canonicalUrl as getCanonicalUrl } from '@/utils/seo';
import { Seo } from '@/components/utils/seo/seo-component';
import { URLS } from '@/config/urls';
import useTranslation from 'next-translate/useTranslation';
import { BLOCKS } from '@contentful/rich-text-types';
import { AcademyOverviewPageProps } from '@/components/features/academy/academy.types';
import { getCategoryUrl } from '@/utils/academy';
import { getGamingGuideUrl } from '@/utils/gaming-guides';
import { getBadgeVariant, isGameNew } from '@/utils/games';
import { getOpenGraphProps } from '@/components/utils/seo/get-open-graph-props';
type PageProps = InferGetStaticPropsType<typeof getStaticProps>;
const formatGameTiles = (games: AcademyPopularGameFieldsFragment[]): TileProps[] => games.map(({ sys, title, mainImage, urlSlug, categories, gameTagsCollection }) => {
    const newBadge = isGameNew(sys.firstPublishedAt) && { name: 'New' };
    return {
        title,
        image: {
            src: mainImage.url,
            alt: mainImage.description || mainImage.title
        },
        tags: categories.flatMap(category => ({ children: category })),
        badges: [...gameTagsCollection?.items, newBadge]
            .filter(Boolean)
            .flatMap(tag => ({ children: tag.name, variant: getBadgeVariant(tag.name) })),
        href: getGamingGuideUrl(urlSlug)
    };
});
const translateGameBadges = (tiles: TileProps[], findLabel: string, replaceLabel: string): TileProps[] => tiles.map(tile => ({
    ...tile,
    badges: tile.badges.map(badge => ({
        ...badge,
        children: badge.children.toString().replace(findLabel, replaceLabel)
    }))
}));
const Page: NextPage<PageProps> = ({ linkedSeoTags, lastUpdate, title, description, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords, articleCategories, popularGames, }) => {
    const router = useRouter();
    const { t } = useTranslation('academy');
    const canonicalUrl = getCanonicalUrl(router);
    return (<BaseLayout>
      <Seo title={linkedSeoTags?.title || metaTitle || title} description={linkedSeoTags?.description || metaDescription || description} nofollow={linkedSeoTags?.noFollow || noFollow} noindex={linkedSeoTags?.noIndex || noIndex} keywords={linkedSeoTags?.keywords || keywords} lastUpdate={lastUpdate} canonical={canonicalUrl} openGraph={getOpenGraphProps({
            title: linkedSeoTags?.title || metaTitle || title,
            description: linkedSeoTags?.description || metaDescription || description,
            url: canonicalUrl,
            image: ogImage
        })}/>
      <TextualHero title={title} subtitle={{
            nodeType: BLOCKS.DOCUMENT,
            data: {},
            content: [
                {
                    nodeType: BLOCKS.HEADING_6,
                    data: {},
                    content: [
                        {
                            nodeType: 'text',
                            value: description,
                            data: {},
                            marks: [{ type: '' }]
                        },
                    ]
                },
            ]
        }}/>
      <TitleSection mb={{ base: 16, md: 24 }} title={t('section-tile-list.title')} as="h2" headingSize="h3" headingVariant="white" actionSlot={<TextLink href={URLS.GAMING_GUIDES} color="lightPurple" rightIcon={{ icon: IconName.arrowRightFill }}>
            {t('section-tile-list.cta-title')}
          </TextLink>}>
        <Container>
          <TileList tiles={translateGameBadges(popularGames, 'New', t('common:labels.new'))}/>
        </Container>
      </TitleSection>
      <TitleSection title={t('section-link-box-list.title')} as="h2" headingSize="h3" headingVariant="white" mb={16}>
        <Container>
          <LinkBoxList>
            {articleCategories.map(({ title, urlSlug, description }, index) => (<LinkBox href={getCategoryUrl(urlSlug)} key={index}>
                <Heading as="h3" size="h4" mb={4}>
                  {title}
                </Heading>
                <Text variant="muted">{description}</Text>
              </LinkBox>))}
          </LinkBoxList>
        </Container>
      </TitleSection>
    </BaseLayout>);
};
const getStaticProps: GetStaticProps<AcademyOverviewPageProps> = async () => {
    const overviewPage = await contentfulClient
        .query<ArticleOverviewPageQuery>(ArticleOverviewPageDocument)
        .toPromise();
    const pageContent = overviewPage.data?.pageAcademyOverviewCollection?.items?.[0];
    const articleCategories = overviewPage.data?.pageAcademyCategoryCollection?.items;
    if (!pageContent) {
        console.warn('👻 Academy page missing');
        return {
            notFound: true
        };
    }
    const { title, description, popularGamesCollection, linkedSeoTags, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords, } = pageContent;
    console.log('✨ Fetched academy page from contentful', new Date());
    return {
        props: {
            linkedSeoTags,
            lastUpdate: `${new Date()}`,
            title,
            description,
            metaTitle,
            metaDescription,
            ogImage,
            noFollow,
            noIndex,
            keywords,
            articleCategories,
            popularGames: formatGameTiles(popularGamesCollection.items)
        },
        revalidate: 5 * 60
    };
};
export default withUrql(Page);

    async function __Next_Translate__getStaticProps__1973b3b27d4__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/academy/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1973b3b27d4__ as getStaticProps }
  